import React, { useState, useCallback } from 'react';

import { Alert, Divider, Typography } from 'antd';

import FileUploader from 'components/FileUploader';
import { useFormikContext } from 'formik';
import { PersonalDocumentType } from 'v2/components/Pages/AddBankAccount/Stepper/form';
import { CurrencyId } from 'v2/interfaces/financial';
import type { INewAccount } from 'v2/interfaces/financial';

interface IProps {
  hasVideo: boolean;
  docType: string;
  mediaPreview: any;
  onChangeDocType: () => void;
  setMediaPreview: (name: string, preview: any) => void;
}

const PersonalDocuments = ({ hasVideo, docType, mediaPreview, onChangeDocType, setMediaPreview }: IProps) => {
  const { values, setSubmitting, setFieldValue } = useFormikContext<INewAccount>();
  const [imgTitle, setImgTitle] = useState(
    'Envie-nos uma foto da sua CNH no campo abaixo. A CNH deve estar aberta, mostrando frente e verso.'
  );
  const [imgError, setImgError] = useState('');
  const pathName = window.location.pathname;
  const isRefundAccount = pathName.includes('bank-account-refund');
  const isBRLSelected = values.currency === CurrencyId.BRL;
  const isUSDSelected = values.currency === CurrencyId.USD;

  const handleUploadEvents = (event: string, message?: string) => {
    if (event === 'uploading') {
      setSubmitting(true);
      return;
    }

    if (event === 'error') {
      setImgError(message);
      setSubmitting(false);
    }
  };

  const handleMediaChange = useCallback(
    (name: string, id: number, preview: any) => {
      setMediaPreview(name, preview);
      setFieldValue('imgsIds', { ...values.imgsIds, [name]: id });
      setSubmitting(false);
    },
    [setFieldValue, setMediaPreview, setSubmitting, values.imgsIds]
  );

  const handleChangeImgLabels = () => {
    setImgTitle(
      docType === PersonalDocumentType.CNH
        ? 'Envie-nos fotos do seu RG e CPF nos campos abaixo.'
        : 'Envie-nos uma foto da sua CNH no campo abaixo. A CNH deve estar aberta, mostrando frente e verso.'
    );
    onChangeDocType();
  };

  return (
    <div className='flex flex-col gap-4'>
      <Typography.Text strong>
        {isUSDSelected
          ? 'Envie-nos fotos do Documento de Constituição de Empresa (Article of Incorporation) ou do Employer Identification Number (EIN) expedido pelo IRS no campo abaixo.'
          : imgTitle}
      </Typography.Text>

      {docType === PersonalDocumentType.CNH ? (
        <FileUploader
          onChange={handleMediaChange}
          fileName='imgDocCNH'
          defaultPreview={mediaPreview['imgDocCNH']}
          type='image'
          uploadType='mpg_docs'
          label={values.currency !== 2 ? 'CNH' : ''}
          onEvent={handleUploadEvents}
          isRefundAccount={isRefundAccount}
        />
      ) : (
        <div className='flex flex-col gap-4'>
          <FileUploader
            onChange={handleMediaChange}
            fileName='imgDocRG'
            defaultPreview={mediaPreview['imgDocRG']}
            type='file'
            uploadType='mpg_docs'
            label='RG'
            onEvent={handleUploadEvents}
            isRefundAccount={isRefundAccount}
          />
          <FileUploader
            onChange={handleMediaChange}
            fileName='imgDocCPF'
            defaultPreview={mediaPreview['imgDocCPF']}
            type='image'
            uploadType='mpg_docs'
            label='CPF'
            onEvent={handleUploadEvents}
            isRefundAccount={isRefundAccount}
          />
        </div>
      )}

      <Typography.Text type='secondary'>
        Envie arquivos nos formatos: JPG, PNG ou PDF. De no máximo 2mb.
      </Typography.Text>

      {isBRLSelected && (
        <Typography.Text>
          {docType === 'cnh' ? 'Não possui CNH?' : 'Possui CNH?'}{' '}
          <Typography.Link onClick={handleChangeImgLabels}>Clique aqui.</Typography.Link>
        </Typography.Text>
      )}

      {hasVideo && (
        <>
          <Divider className='my-0' />
          <Typography.Text strong>
            Envie-nos um vídeo com 3 a 7 segundos de duração, onde você apareça segurando o documento enviado acima com
            a frente (lado da foto) virado para a câmera no campo abaixo.
          </Typography.Text>
          <FileUploader
            onChange={handleMediaChange}
            fileName='videoDoc'
            defaultPreview={mediaPreview['videoDoc']}
            type='video'
            uploadType='mpg_docs'
            label='Vídeo selfie'
            onEvent={handleUploadEvents}
            isRefundAccount={isRefundAccount}
          />
          <Typography.Text type='secondary'>
            Envie arquivos nos formatos: AVI, MP4, MKV, MOV, WMV ou WEBM. De no máximo 35mb.
          </Typography.Text>
        </>
      )}

      {imgError && <Alert type='error' showIcon message={imgError} />}
    </div>
  );
};

export default PersonalDocuments;
